import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AdminFeedbacks.css';
import privateAxios from '../../utils/axios/privateAxios';
import { FaTrash } from 'react-icons/fa';
import moment from 'moment';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';

const AdminFeedbacks = () => {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedFeedbacks, setSelectedFeedbacks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: '',
    onConfirm: null,
    loading: false,
  });

  const fetchFeedbacks = async (page = 1) => {
    try {
      setLoading(true);
      const response = await privateAxios.get(`/private/admin-feedbacks?page=${page}`);
      setFeedbacks(response.data.feedbacks);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching feedbacks', error);
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchedFeedbacks = async (page = 1) => {
    try {
      setLoading(true);
      const response = await privateAxios.get(`/private/admin-search-feedbacks?query=${searchTerm.trim()}&page=${page}`);
      setFeedbacks(response.data.feedbacks);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error searching feedbacks', error);
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setSearchMode(false);
      fetchFeedbacks(1);
    } else {
      setSearchMode(true);
      fetchSearchedFeedbacks(1);
    }
  };

  const handleCheckbox = (id) => {
    setSelectedFeedbacks((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const deleteSelectedFeedbacks = async () => {
    if (!selectedFeedbacks.length) return;
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to delete ${selectedFeedbacks.length} selected feedback(s)?`,
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          await privateAxios.post('/private/delete-selected-feedbacks', { ids: selectedFeedbacks });
          setSelectedFeedbacks([]);
          setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
          searchMode ? fetchSearchedFeedbacks(currentPage) : fetchFeedbacks(currentPage);
        } catch (error) {
          console.error('Error deleting feedbacks', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false
    });
  };

  const handleDeleteSingle = (id, title) => {
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to delete the feedback titled "${title}"?`,
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          await privateAxios.delete(`/private/delete-feedback/${id}`);
          setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
          searchMode ? fetchSearchedFeedbacks(currentPage) : fetchFeedbacks(currentPage);
        } catch (error) {
          console.error('Error deleting feedback', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false
    });
  };

  const handleClickFeedback = (id, e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    navigate(`/admin-feedback/${id}`);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      searchMode ? fetchSearchedFeedbacks(nextPage) : fetchFeedbacks(nextPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      searchMode ? fetchSearchedFeedbacks(prevPage) : fetchFeedbacks(prevPage);
    }
  };

  return (
    <div className="admin-feedbacks">
      <div className="admin-feedbacks-header">
        <div className='admin-feedbacks-search-input'>
          <input
            type="text"
            placeholder="Search feedbacks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className='admin-feedbacks-search-btn' onClick={handleSearch}>Search</button>
        </div>

        <button
          className="admin-feedbacks-delete-selected-btn"
          style={{ backgroundColor: selectedFeedbacks.length ? '#008751' : '#ccf1e4' }}
          onClick={deleteSelectedFeedbacks}
          disabled={!selectedFeedbacks.length}
        >
          Delete Selected
        </button>
      </div>

      <div className="admin-feedbacks-list">
        {loading ? (
          <div>Loading...</div>
        ) : feedbacks.length ? (
          feedbacks.map((f) => (
            <div className="admin-feedbacks-card" key={f._id} onClick={(e) => handleClickFeedback(f._id, e)}>
              <div className='admin-feedbacks-checkbox-title'>
                <input
                  type="checkbox"
                  checked={selectedFeedbacks.includes(f._id)}
                  onChange={() => handleCheckbox(f._id)}
                />
                <div className="feedbacks-info">
                  <h4>
                    {f.title.split(' ').length > 10
                      ? f.title.split(' ').slice(0, 10).join(' ') + '...'
                      : f.title}
                  </h4>

                  <small>
                    By: {f.createdBy?.firstname} {f.createdBy?.lastname} | Created: {moment(f.createdAt).format('MMMM DD, YYYY')}
                    {f.updatedAt && ` | Last Edited: ${new Date(f.updatedAt).toLocaleDateString()}`}
                  </small>
                </div>
              </div>

              <div className="admin-feedbacks-actions">
                <button
                  className='admin-feedbacks-delete-button'
                  onClick={() => handleDeleteSingle(f._id, f.title)}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div>No feedbacks found.</div>
        )}
      </div>

      <div className="admin-feedbacks-pagination-controls">
        <button onClick={handlePreviousPage} disabled={currentPage === 1}>Previous</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
      </div>
      <div className="admin-feedbacks-back-to-admin-dashboard">
        <Link to={'/admin-dashboard'}>Back to Admin Dashboard</Link>
      </div>

      {confirmDialog.open && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false })}
          loading={confirmDialog.loading}
        />
      )}
    </div>
  );
};

export default AdminFeedbacks;